import { LocalDate } from "@js-joda/core";
import type { Messages } from "../../../core/api/api-utils";
import type {
  ICD10CodeId,
  PatientContractDiagnosisCodeId,
  PatientContractId,
} from "../../../shared/schema/schema";

// Pure form logic of the patient-contract diagnosis (ICD) code editor, mirroring the classic
// `patientContractDiagnosisCodeSetupModalCtrl.js`. Node-test friendly (no React / app runtime
// imports).

export type PatientContractDiagnosisCode = Messages["PatientContractDiagnosisCode"];

/** `PatientContractDiagnosisCodeCreateParams` (POST, a classic route: not in the schema). */
export type ContractDiagnosisCodeCreateBody = Pick<
  PatientContractDiagnosisCode,
  "icdCode" | "patientContractId" | "startDate" | "endDate" | "type" | "isForBilling"
>;

/** `PatientContractDiagnosisCodeEditParams` (PUT): "is for billing" cannot be changed. */
export type ContractDiagnosisCodeEditBody = Partial<
  Omit<ContractDiagnosisCodeCreateBody, "isForBilling">
>;

/** The classic modal's choices; "OTHER" is stored as `null`. */
export type DiagnosisTypeChoice = "PRINCIPAL" | "SURGICAL" | "OTHER";

export const DIAGNOSIS_TYPE_OPTIONS: ReadonlyArray<{ id: DiagnosisTypeChoice; label: string }> = [
  { id: "PRINCIPAL", label: "Principal diagnosis" },
  { id: "SURGICAL", label: "Surgical procedure" },
  { id: "OTHER", label: "Other pertinent diagnosis" },
];

export function diagnosisTypeLabel(type: PatientContractDiagnosisCode["type"]): string {
  return DIAGNOSIS_TYPE_OPTIONS.find((option) => option.id === (type ?? "OTHER"))?.label ?? "";
}

export interface SelectedIcdCode {
  formattedDxCode: string;
  id: ICD10CodeId;
  shortDesc: string;
}

export interface ContractDiagnosisCodeForm {
  endDate: LocalDate | null;
  icdCode: SelectedIcdCode | null;
  isForBilling: boolean;
  patientContractId: PatientContractId | null;
  startDate: LocalDate | null;
  type: DiagnosisTypeChoice;
}

export type ContractDiagnosisCodeFormErrors = Partial<
  Record<"endDate" | "icdCode" | "patientContractId" | "type", string>
>;

/**
 * The contract a new code starts with: the task's contract when it is a choice, else the only
 * choice, else none.
 */
export function defaultContractId(
  contractChoices: ReadonlyArray<{ id: PatientContractId }>,
  preferredContractId: PatientContractId | null
): PatientContractId | null {
  if (preferredContractId !== null && contractChoices.some((c) => c.id === preferredContractId)) {
    return preferredContractId;
  }
  return contractChoices.length === 1 ? contractChoices[0].id : null;
}

/** A code to edit, or a new one (contract from `defaultContractId`). */
export function initialContractDiagnosisCodeForm(
  code: PatientContractDiagnosisCode | null,
  contractChoices: ReadonlyArray<{ id: PatientContractId }>,
  preferredContractId: PatientContractId | null
): ContractDiagnosisCodeForm {
  if (code === null) {
    return {
      endDate: null,
      icdCode: null,
      isForBilling: false,
      patientContractId: defaultContractId(contractChoices, preferredContractId),
      startDate: null,
      type: "OTHER",
    };
  }
  return {
    endDate: code.endDate,
    icdCode: { formattedDxCode: code.formattedDxCode, id: code.icdCode, shortDesc: code.shortDesc },
    isForBilling: code.isForBilling,
    patientContractId: code.patientContractId,
    startDate: code.startDate,
    type: code.type ?? "OTHER",
  };
}

/**
 * Code and contract are required (as in the classic modal). Also rejects an end date before the
 * start date, and a second principal / surgical code on one contract (the table is unique on
 * `(patient_contract_id, diagnosis_type)`, so the server would fail the write).
 */
export function validateContractDiagnosisCodeForm(
  form: ContractDiagnosisCodeForm,
  existingCodes: ReadonlyArray<PatientContractDiagnosisCode>,
  editingId: PatientContractDiagnosisCodeId | null
): ContractDiagnosisCodeFormErrors {
  const errors: ContractDiagnosisCodeFormErrors = {};
  if (form.icdCode === null) {
    errors.icdCode = "Select an ICD code from the list";
  }
  if (form.patientContractId === null) {
    errors.patientContractId = "Select a contract";
  }
  if (form.startDate !== null && form.endDate !== null && form.endDate.isBefore(form.startDate)) {
    errors.endDate = "End date must be on or after the start date";
  }
  const storedType = toStoredType(form.type);
  if (storedType !== null && form.patientContractId !== null) {
    const taken = existingCodes.some(
      (code) =>
        code.id !== editingId &&
        code.patientContractId === form.patientContractId &&
        code.type === storedType
    );
    if (taken) {
      errors.type = `This contract already has a ${diagnosisTypeLabel(storedType).toLowerCase()}`;
    }
  }
  return errors;
}

export function hasFormErrors(errors: ContractDiagnosisCodeFormErrors): boolean {
  return Object.values(errors).some((error) => error !== undefined);
}

function toStoredType(type: DiagnosisTypeChoice): PatientContractDiagnosisCode["type"] {
  switch (type) {
    case "PRINCIPAL":
    case "SURGICAL":
      return type;
    case "OTHER":
      return null;
  }
}

/** The POST body; `null` while the form misses its required fields. */
export function toCreateBody(
  form: ContractDiagnosisCodeForm
): ContractDiagnosisCodeCreateBody | null {
  if (form.icdCode === null || form.patientContractId === null) {
    return null;
  }
  return {
    endDate: form.endDate,
    icdCode: form.icdCode.id,
    isForBilling: form.isForBilling,
    patientContractId: form.patientContractId,
    startDate: form.startDate,
    type: toStoredType(form.type),
  };
}

/** The PUT body: every editable field (a `null` date clears it); `null` while incomplete. */
export function toEditBody(form: ContractDiagnosisCodeForm): ContractDiagnosisCodeEditBody | null {
  const body = toCreateBody(form);
  if (body === null) {
    return null;
  }
  return {
    endDate: body.endDate,
    icdCode: body.icdCode,
    patientContractId: body.patientContractId,
    startDate: body.startDate,
    type: body.type,
  };
}

// ---- Contracts ----

export interface ContractChoice {
  contractTypeName: string;
  endDate: LocalDate | null;
  id: PatientContractId;
  isOnHold: boolean | null;
  isPrimary: boolean | null;
  startDate: LocalDate;
}

/** The classic `onlyActivePatientContracts` filter: not ended (end date after today), not on hold. */
export function isActiveContract(contract: ContractChoice, today: LocalDate): boolean {
  return (
    (contract.endDate === null || contract.endDate.isAfter(today)) && contract.isOnHold !== true
  );
}

/**
 * The active contracts (as the classic modal offers), plus `keepContractIds` even when inactive:
 * the edited code's contract (editing it does not force a contract change) and the task's.
 */
export function contractChoices<T extends ContractChoice>(
  contracts: ReadonlyArray<T>,
  today: LocalDate,
  keepContractIds: ReadonlyArray<PatientContractId>
): T[] {
  return contracts.filter(
    (contract) => isActiveContract(contract, today) || keepContractIds.includes(contract.id)
  );
}

/** The classic `formatPatientContract` filter: "Name | MM/dd/yyyy - MM/dd/yyyy|Present (flags)". */
export function formatContractLabel(contract: ContractChoice, today: LocalDate): string {
  const end = contract.endDate === null ? "Present" : formatUsDate(contract.endDate);
  const flags: string[] = [];
  if (contract.isPrimary === true) {
    flags.push("Primary");
  }
  if (contract.isOnHold === true) {
    flags.push("On Hold");
  }
  if (!isActiveContract(contract, today)) {
    flags.push("Inactive");
  }
  const label = `${contract.contractTypeName} | ${formatUsDate(contract.startDate)} - ${end}`;
  return flags.length > 0 ? `${label} (${flags.join(", ")})` : label;
}

function formatUsDate(date: LocalDate): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.monthValue())}/${pad(date.dayOfMonth())}/${date.year()}`;
}

// ---- List order ----

function pdfTypeRank(type: PatientContractDiagnosisCode["type"]): number {
  switch (type) {
    case "SURGICAL":
      return 0;
    case "PRINCIPAL":
      return 1;
    case null:
      return 2;
  }
}

/**
 * The order the generated PDF lists the codes in: the server's
 * `ORDER BY diagnosis_type = 'PRINCIPAL', diagnosis_type = 'SURGICAL', created_at DESC`
 * (getPatientContractDiagnosisCodeByTaskInstanceId) puts false, true, then NULL first ->
 * surgical, principal, other; newest first within each.
 */
export function sortCodesLikePdf<
  T extends Pick<PatientContractDiagnosisCode, "createdAt" | "id" | "type">
>(codes: ReadonlyArray<T>): T[] {
  return [...codes].sort(
    (a, b) =>
      pdfTypeRank(a.type) - pdfTypeRank(b.type) || b.createdAt.compareTo(a.createdAt) || b.id - a.id
  );
}
